import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

type AuthGuardProps = {
  children: ReactNode;
  requiredRoles?: number[]; // Array of role IDs that can access this route
};

const AuthGuard = ({ children, requiredRoles = [] }: AuthGuardProps) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Define public routes that don't require authentication
  const publicRoutes = [
    "/admin/login",
    "/admin/standard-user",
    "/admin/reset-password",
    "/reset-password",
    "/auth/invite",
    "/auth/v1/verify",
    "/verify",
    "/admin/setup",
  ];

  // Skip auth check for public pages
  if (publicRoutes.includes(location.pathname)) {
    return <>{children}</>;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Check if user is a standard user (only has role ID 5)
  const isStandardUser = user.roleIds?.length === 1 && user.roleIds[0] === 5;

  // If standard user trying to access any page except the standard user page, redirect them
  if (isStandardUser && location.pathname !== "/admin/standard-user") {
    return <Navigate to="/admin/standard-user" replace />;
  }

  // Check for required roles if specified
  if (requiredRoles.length > 0) {
    // Allow super admin (role ID 1) to access everything
    const isSuperAdmin = user.roleIds?.includes(1);

    // Check if user has any of the required roles
    const hasRequiredRole = requiredRoles.some((roleId) =>
      user.roleIds?.includes(roleId)
    );

    if (!isSuperAdmin && !hasRequiredRole) {
      return <Navigate to="/admin/dashboard" replace />;
    }
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

export default AuthGuard;
